import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw, Search, UserPlus, Users, X, Edit2, Trash2,
  CheckCircle, AlertCircle, ChevronDown, Filter, FilterX,
  FileSpreadsheet, Phone, Smartphone
} from 'lucide-react';
import * as XLSX from 'xlsx';
import apiClient from '../api/client';
import { useAuth } from '../AuthContext';
import './DashboardBultos.css';
import './Pedidos.css';

/* ---------- MULTISELECT FILTER (same pattern as Pedidos) ---------- */
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasSelection = selectedValues?.length > 0;
  const showSearch = options.length > 8;
  const filteredOptions = options
    .filter(opt => String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 100);

  const areAllSelected = filteredOptions.every(opt => selectedValues.includes(opt.value || opt));
  const displayValue = hasSelection
    ? (selectedValues.length === 1
      ? options.find(o => (o.value || o) === selectedValues[0])?.label || selectedValues[0]
      : `${selectedValues.length} Selec.`)
    : 'Todos';

  const handleSelectAll = () => {
    if (areAllSelected) {
      onChange(name, selectedValues.filter(val => !filteredOptions.some(o => (o.value || o) === val)));
    } else {
      onChange(name, filteredOptions.map(o => o.value || o));
    }
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${hasSelection ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        {Icon && <Icon size={12} className="pill-icon" />}
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input type="text" placeholder="Buscar..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} autoFocus />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input type="checkbox" checked={selectedValues.includes(optValue)}
                    onChange={() => {
                      const newVals = selectedValues.includes(optValue)
                        ? selectedValues.filter(v => v !== optValue)
                        : [...selectedValues, optValue];
                      onChange(name, newVals);
                    }} />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- TOAST SIMPLE ---------- */
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  return { toasts, addToast };
};

const ToastContainer = ({ toasts }) => (
  <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        padding: '12px 18px', borderRadius: '10px', fontWeight: 600, fontSize: '0.875rem',
        background: t.type === 'success' ? 'rgba(0, 210, 100, 0.15)' : 'rgba(239, 68, 68, 0.15)',
        color: t.type === 'success' ? '#00d26a' : '#f87171',
        border: `1px solid ${t.type === 'success' ? 'rgba(0,210,100,0.3)' : 'rgba(239,68,68,0.3)'}`,
        display: 'flex', alignItems: 'center', gap: '8px',
        backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        animation: 'fadeIn 0.2s ease',
      }}>
        {t.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
        {t.message}
      </div>
    ))}
  </div>
);

/* ---------- HELPERS REGIÓN VENTA ---------- */
const parseRegions = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return val.split(',').map(s => s.trim()).filter(Boolean);
};
const joinRegions = (arr) => arr.join(',');

/* ---------- VALIDADORES ---------- */
const formatPhoneInput = (val) => val.replace(/[^\d+\-\s()]/g, '').slice(0, 20);
const formatImeiInput = (val) => val.replace(/\D/g, '').slice(0, 16);

/* ---------- CAMPOS DEL FORMULARIO ---------- */
const EMPTY_FORM = {
  nombre: '', email: '', username: '', password: '',
  cargo: '', area: '', subarea1: '', subarea2: '', rol: '',
  region_venta: '', region: '', cargo2: '',
  telefono: '', imei: ''
};

/* ---------- OPCIONES FIJAS ---------- */
const REGION_VENTA_OPTIONS = [
  'ANDES (TACHIRA MERIDA TRUJILLO)',
  'CAPITAL (MIRANDA VARGAS)',
  'CENTRO (GUARICO ARAGUA CARABOBO COJEDES)',
  'CENTRO OCCIDENTE (LARA BARINAS APURE PORTUGUESA YARACUY)',
  'INSULAR (NUEVA ESPARTA)',
  'OCCIDENTE (FALCON ZULIA)',
  'ORIENTE NORTE (SUCRE  ANZOATEGUI)',
  'ORIENTE SUR (BOLIVAR MONAGAS DELTA A)',
];

const CARGO2_OPTIONS = [
  'Gerente Nacional',
  'Gerente Regional',
  'Coordinador',
  'Analista',
  'Especialista',
  'Asesor',
];

/* ---------- COMPONENTE PRINCIPAL ---------- */
const AdminUsuarios = () => {
  const { profile } = useAuth();
  const { toasts, addToast } = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchText, setSearchText] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filterCargo, setFilterCargo] = useState([]);
  const [filterArea, setFilterArea] = useState([]);
  const [filterRegionVenta, setFilterRegionVenta] = useState([]);
  const [filterCargo2, setFilterCargo2] = useState([]);
  const [filterRegion, setFilterRegion] = useState([]);
  const [filterUsuario, setFilterUsuario] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  /* Listas únicas para filtros */
  const cargoOptions = [...new Set(usuarios.map(u => u.cargo).filter(Boolean))].sort()
    .map(c => ({ value: c, label: c }));
  const areaOptions = [...new Set(usuarios.map(u => u.area).filter(Boolean))].sort()
    .map(a => ({ value: a, label: a }));
  const regionVentaOptions = REGION_VENTA_OPTIONS.map(r => ({ value: r, label: r }));
  const cargo2Options = CARGO2_OPTIONS.map(c => ({ value: c, label: c }));
  const regionOptions = [...new Set(usuarios.map(u => u.region).filter(Boolean))].sort()
    .map(r => ({ value: r, label: r }));
  const usuarioOptions = [...usuarios]
    .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
    .filter(u => u.username)
    .map(u => ({ value: u.username, label: `${u.nombre || u.username} (@${u.username})` }));

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/usuarios');
      setUsuarios(data);
    } catch {
      addToast('Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  /* Filtrado local */
  const filteredUsuarios = usuarios.filter(u => {
    const text = searchText.toLowerCase();
    const matchSearch = !text ||
      u.nombre?.toLowerCase().includes(text) ||
      u.username?.toLowerCase().includes(text) ||
      u.email?.toLowerCase().includes(text) ||
      u.cargo?.toLowerCase().includes(text) ||
      u.region_venta?.toLowerCase().includes(text) ||
      u.cargo2?.toLowerCase().includes(text) ||
      u.region?.toLowerCase().includes(text) ||
      u.telefono?.toLowerCase().includes(text) ||
      u.imei?.toLowerCase().includes(text);
    const matchCargo = filterCargo.length === 0 || filterCargo.includes(u.cargo);
    const matchArea = filterArea.length === 0 || filterArea.includes(u.area);
    const matchRegionVenta = filterRegionVenta.length === 0 ||
      parseRegions(u.region_venta).some(r => filterRegionVenta.includes(r));
    const matchCargo2 = filterCargo2.length === 0 || filterCargo2.includes(u.cargo2);
    const matchRegion = filterRegion.length === 0 || filterRegion.includes(u.region);
    const matchUsuario = filterUsuario.length === 0 || filterUsuario.includes(u.username);
    return matchSearch && matchCargo && matchArea && matchRegionVenta && matchCargo2 && matchRegion && matchUsuario;
  });

  const handleOpenModal = (user = null) => {
    setEditingUser(user);
    setFormData(user ? { ...EMPTY_FORM, ...user } : EMPTY_FORM);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación IMEI (15 dígitos si se ingresó)
    if (formData.imei && !/^\d{15}$/.test(formData.imei)) {
      addToast('El IMEI debe tener exactamente 15 dígitos', 'error');
      return;
    }

    try {
      if (editingUser) {
        await apiClient.put(`/usuarios/${editingUser.id}`, formData);
        addToast('Usuario actualizado correctamente');
      } else {
        const response = await apiClient.post('/usuarios', formData);
        if (response.data?.allowed_paths && response.data.allowed_paths.length > 1) {
          addToast(`Usuario creado con permisos heredados del cargo "${formData.cargo}"`);
        } else {
          addToast('Usuario creado correctamente');
        }
      }
      handleCloseModal();
      fetchUsuarios();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al guardar usuario', 'error');
    }
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar a "${nombre}"?`)) return;
    setDeletingId(id);
    try {
      await apiClient.delete(`/usuarios/${id}`);
      addToast('Usuario eliminado');
      fetchUsuarios();
    } catch {
      addToast('Error al eliminar usuario', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetFilters = () => {
    setFilterCargo([]);
    setFilterArea([]);
    setFilterRegionVenta([]);
    setFilterCargo2([]);
    setFilterRegion([]);
    setFilterUsuario([]);
    setSearchText('');
  };

  /* ---------- EXPORTAR A EXCEL ---------- */
  const handleExportExcel = () => {
    if (filteredUsuarios.length === 0) {
      addToast('No hay usuarios para exportar', 'error');
      return;
    }

    const rows = filteredUsuarios.map(u => ({
      'ID':           u.id ?? '',
      'Nombre':       u.nombre || '',
      'Usuario':      u.username || '',
      'Email':        u.email || '',
      'Contraseña':   u.password || '',
      'Teléfono':     u.telefono || '',
      'IMEI':         u.imei || '',
      'Cargo':        u.cargo || '',
      'Cargo 2':      u.cargo2 || '',
      'Región Venta': parseRegions(u.region_venta).join(' | '),
      'Región':       u.region || '',
      'Área':         u.area || '',
      'Sub Área 1':   u.subarea1 || '',
      'Sub Área 2':   u.subarea2 || '',
      'Rol':          u.rol || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);

    const headers = Object.keys(rows[0]);
    worksheet['!cols'] = headers.map(h => {
      const maxLen = Math.max(h.length, ...rows.map(r => String(r[h] || '').length));
      return { wch: Math.min(maxLen + 2, 50) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

    const fecha = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Usuarios_${fecha}.xlsx`);

    addToast(`Exportados ${rows.length} usuarios a Excel`);
  };

  const cargoColor = (cargo) => {
    if (!cargo) return { bg: 'rgba(255,255,255,0.08)', color: 'var(--text-third)' };
    const h = cargo.charCodeAt(0) % 6;
    const colors = [
      { bg: '#2dd4bf', color: 'white' },
      { bg: '#fbbf24', color: 'white' },
      { bg: '#f87171', color: 'white' },
      { bg: '#a78bfa', color: 'white' },
      { bg: '#60a5fa', color: 'white' },
      { bg: '#34d399', color: 'white' },
    ];
    return colors[h];
  };

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <ToastContainer toasts={toasts} />

      {/* HEADER */}
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Gestión de Usuarios</h1>
          <div className="title-underline"></div>
        </div>
        <div className="date-selectors">
          <button className="refresh-button" onClick={fetchUsuarios} disabled={loading} title="Refrescar">
            <RefreshCw size={16} className={loading ? 'spin-anim' : ''} />
          </button>
          <button className="new-order-btn" onClick={() => handleOpenModal()}>
            <UserPlus size={17} />
            <span style={{ marginLeft: '8px' }}>Nuevo Usuario</span>
          </button>
          <button
            className="new-order-btn"
            onClick={handleExportExcel}
            disabled={loading || filteredUsuarios.length === 0}
            title="Descargar Excel"
            style={{
              background: '#10b981',
              boxShadow: '0 4px 12px rgba(16,185,129,0.4)',
              opacity: (loading || filteredUsuarios.length === 0) ? 0.5 : 1,
              cursor: (loading || filteredUsuarios.length === 0) ? 'not-allowed' : 'pointer'
            }}
          >
            <FileSpreadsheet size={17} />
            <span style={{ marginLeft: '8px' }}>Excel</span>
          </button>
          <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
            <Filter size={16} /><span>Filtros</span>
          </button>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', opacity: 0.5 }} />
            <input
              type="text"
              placeholder="Buscar usuario, tel, IMEI..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{
                paddingLeft: '32px', paddingRight: '12px', paddingTop: '6px', paddingBottom: '6px',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
                background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)',
                fontSize: '0.85rem', outline: 'none', width: '220px'
              }}
            />
          </div>
        </div>
      </header>

      {/* FILTROS LATERALES MÓVIL */}
      <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
        <div className="filters-sidebar-header">
          <h3>Filtros</h3>
          <div className="filters-header-actions">
            <button className="reset-filters-btn" onClick={handleResetFilters}><FilterX size={16} /></button>
            <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}><X size={20} /></button>
          </div>
        </div>
        <div className="filters-grid" style={{ gap: '8px' }}>
          <MultiSelectFilter label="Usuario" name="usuario" icon={Users} options={usuarioOptions} selectedValues={filterUsuario}
            onChange={(_, vals) => setFilterUsuario(vals)} />
          <MultiSelectFilter label="Cargo" name="cargo" icon={Users} options={cargoOptions} selectedValues={filterCargo}
            onChange={(_, vals) => setFilterCargo(vals)} />
          <MultiSelectFilter label="Área" name="area" icon={Filter} options={areaOptions} selectedValues={filterArea}
            onChange={(_, vals) => setFilterArea(vals)} />
          <MultiSelectFilter label="Región Venta" name="region_venta" icon={Filter} options={regionVentaOptions} selectedValues={filterRegionVenta}
            onChange={(_, vals) => setFilterRegionVenta(vals)} />
          <MultiSelectFilter label="Cargo 2" name="cargo2" icon={Users} options={cargo2Options} selectedValues={filterCargo2}
            onChange={(_, vals) => setFilterCargo2(vals)} />
          {regionOptions.length > 0 && (
            <MultiSelectFilter label="Región" name="region" icon={Filter} options={regionOptions} selectedValues={filterRegion}
              onChange={(_, vals) => setFilterRegion(vals)} />
          )}
          <button className="filter-pill-button reset-filters" onClick={handleResetFilters}><FilterX size={12} /><span>Limpiar</span></button>
        </div>
      </div>
      {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

      {/* KPI CARDS */}
      <div className="dashboard-indicators3" style={{ padding: '15px', background: 'transparent' }}>
        <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper"><Users size={22} color="white" /></div>
            <span className="indicator-label">Total Usuarios</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{usuarios.length}</span>
            </div>
          </div>
        </div>
        <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper"><Filter size={22} color="white" /></div>
            <span className="indicator-label">Filtrados</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{filteredUsuarios.length}</span>
            </div>
          </div>
        </div>
        <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper"><CheckCircle size={22} color="white" /></div>
            <span className="indicator-label">Cargos Únicos</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{cargoOptions.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* TABLA */}
      <div className="detail-table-container" style={{ marginTop: '20px', opacity: loading ? 0.7 : 1, transition: 'opacity 0.3s' }}>
        <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Directorio de Usuarios</h3>
          <span style={{ fontSize: '0.85rem', opacity: 0.6 }}>
            {filteredUsuarios.length} de {usuarios.length} usuarios
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', opacity: 0.5 }}>
            <RefreshCw size={28} className="spin-anim" style={{ marginBottom: '12px', display: 'block', margin: '0 auto 12px' }} />
            Cargando usuarios...
          </div>
        ) : filteredUsuarios.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', opacity: 0.5 }}>
            No se encontraron usuarios con esos criterios.
          </div>
        ) : (
          <div className="responsive-table-wrapper" style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <table className="detail-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Usuario / Email</th>
                  <th>Contraseña</th>
                  <th>Teléfono</th>
                  <th>IMEI</th>
                  <th>Cargo</th>
                  <th>Cargo 2</th>
                  <th>Región Venta</th>
                  <th>Región</th>
                  <th>Área / Sub</th>
                  <th>Rol</th>
                  <th style={{ textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map(user => {
                  const col = cargoColor(user.cargo);
                  return (
                    <tr key={user.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 'bold', color: 'white'
                          }}>
                            {user.nombre ? user.nombre.split(' ').map(n => n[0]).slice(0, 2).join('') : 'U'}
                          </div>
                          <span style={{ fontWeight: 600 }}>{user.nombre}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>@{user.username}</div>
                        <div style={{ fontSize: '0.78rem', opacity: 0.6, marginTop: '2px' }}>{user.email}</div>
                      </td>
                      <td>
                        <span style={{
                          fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600,
                          padding: '3px 8px', borderRadius: '4px',
                          background: 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: '#fbbf24'
                        }}>
                          {user.password || '—'}
                        </span>
                      </td>

                      {/* TELÉFONO */}
                      <td>
                        {user.telefono ? (
                          <a
                            href={`tel:${user.telefono}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              color: '#34d399', textDecoration: 'none', fontWeight: 600,
                              fontSize: '0.82rem', fontFamily: 'monospace'
                            }}
                            title="Llamar"
                          >
                            <Phone size={12} /> {user.telefono}
                          </a>
                        ) : <span style={{ opacity: 0.3 }}>—</span>}
                      </td>

                      {/* IMEI */}
                      <td>
                        {user.imei ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            fontFamily: 'monospace', fontSize: '0.78rem',
                            padding: '3px 8px', borderRadius: '6px',
                            background: 'rgba(167,139,250,0.15)', color: '#c4b5fd',
                            border: '1px solid rgba(167,139,250,0.25)'
                          }} title={user.imei}>
                            <Smartphone size={12} /> {user.imei}
                          </span>
                        ) : <span style={{ opacity: 0.3 }}>—</span>}
                      </td>

                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700,
                          background: col.bg, color: col.color, whiteSpace: 'nowrap'
                        }}>
                          {user.cargo || 'Sin cargo'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem', opacity: user.cargo2 ? 1 : 0.3 }}>
                        {user.cargo2 || '—'}
                      </td>
                      <td style={{ fontSize: '0.78rem', maxWidth: '220px' }}>
                        {parseRegions(user.region_venta).length > 0
                          ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {parseRegions(user.region_venta).map(r => (
                                <span key={r} style={{
                                  padding: '2px 8px', borderRadius: '6px',
                                  background: 'rgba(59,130,246,0.15)', color: '#93c5fd',
                                  fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap'
                                }}>{r}</span>
                              ))}
                            </div>
                          : <span style={{ opacity: 0.3 }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem', opacity: user.region ? 1 : 0.3 }}>
                        {user.region || '—'}
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {user.area && <div style={{ opacity: 0.9 }}>{user.area}</div>}
                        {user.subarea1 && <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>{user.subarea1}</div>}
                        {user.subarea2 && <div style={{ opacity: 0.5, fontSize: '0.72rem' }}>{user.subarea2}</div>}
                        {!user.area && !user.subarea1 && <span style={{ opacity: 0.3 }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem', opacity: user.rol ? 1 : 0.3 }}>
                        {user.rol || '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button onClick={() => handleOpenModal(user)}
                            title="Editar"
                            style={{
                              padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                              background: '#60a5fa', color: 'white', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: 600
                            }}>
                            <Edit2 size={13} /> Editar
                          </button>
                          <button onClick={() => handleDelete(user.id, user.nombre)}
                            disabled={deletingId === user.id}
                            title="Eliminar"
                            style={{
                              padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                              background: '#f87171', color: 'white', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: 600,
                              opacity: deletingId === user.id ? 0.5 : 1
                            }}>
                            <Trash2 size={13} /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="table-footer-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 12px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.5, fontSize: '0.82rem' }}>
            <Users size={14} /><span>{filteredUsuarios.length} usuarios mostrados</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.5, fontSize: '0.82rem' }}>
            <span>Administrado por: {profile?.nombre || 'Administrador'}</span>
          </div>
        </div>
      </div>

      {/* MODAL CREAR / EDITAR */}
      {modalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)'
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            style={{
              background: 'var(--bg-sidebar)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px', padding: '2rem', width: '90%', maxWidth: '560px',
              maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.6)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {editingUser ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}
              </h2>
              <button onClick={handleCloseModal}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', opacity: 0.6 }}>
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Fila 1: Nombre y Username */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={labelStyle}>Nombre Completo *</label>
                  <input required style={inputStyle} type="text" placeholder="Ej: Juan Pérez"
                    value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Nombre de Usuario *</label>
                  <input required style={inputStyle} type="text" placeholder="Ej: jperez"
                    value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Correo Electrónico *</label>
                <input required style={inputStyle} type="email" placeholder="juan.perez@empresa.com"
                  value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Contraseña</label>
                <input
                  style={{
                    ...inputStyle,
                    background: 'rgba(251, 191, 36, 0.05)',
                    border: '1px solid rgba(251, 191, 36, 0.2)'
                  }}
                  type="text"
                  placeholder="Contraseña visible"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>

              {/* Teléfono + IMEI */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={labelStyle}>
                    <Phone size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Teléfono
                  </label>
                  <input
                    style={inputStyle}
                    type="tel"
                    placeholder="+58 412-1234567"
                    value={formData.telefono}
                    onChange={e => setFormData({ ...formData, telefono: formatPhoneInput(e.target.value) })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    <Smartphone size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    IMEI Dispositivo
                  </label>
                  <input
                    style={{
                      ...inputStyle,
                      fontFamily: 'monospace',
                      letterSpacing: '0.05em',
                      borderColor: formData.imei && formData.imei.length !== 15
                        ? 'rgba(239,68,68,0.5)'
                        : 'rgba(255,255,255,0.1)'
                    }}
                    type="text"
                    inputMode="numeric"
                    placeholder="15 dígitos"
                    value={formData.imei}
                    onChange={e => setFormData({ ...formData, imei: formatImeiInput(e.target.value) })}
                  />
                  {formData.imei && formData.imei.length !== 15 && (
                    <div style={{ fontSize: '0.7rem', color: '#f87171', marginTop: '4px' }}>
                      {formData.imei.length}/15 dígitos
                    </div>
                  )}
                </div>
              </div>

              {/* Cargo y Rol */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={labelStyle}>Cargo *</label>
                  <input required style={inputStyle} type="text" placeholder="Ej: ASESOR COMERCIAL"
                    value={formData.cargo} onChange={e => setFormData({ ...formData, cargo: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Rol Secundario</label>
                  <input style={inputStyle} type="text" placeholder="Ej: SUPERVISOR"
                    value={formData.rol} onChange={e => setFormData({ ...formData, rol: e.target.value })} />
                </div>
              </div>

              {/* Área, Sub Área 1, Sub Área 2 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={labelStyle}>Área</label>
                  <input style={inputStyle} type="text" placeholder="Ventas..."
                    value={formData.area} onChange={e => setFormData({ ...formData, area: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Sub Área 1</label>
                  <input style={inputStyle} type="text" placeholder="Norte..."
                    value={formData.subarea1} onChange={e => setFormData({ ...formData, subarea1: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Sub Área 2</label>
                  <input style={inputStyle} type="text" placeholder="Región..."
                    value={formData.subarea2} onChange={e => setFormData({ ...formData, subarea2: e.target.value })} />
                </div>
              </div>

              {/* Región Venta multi-checkbox */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Región de Venta <span style={{ opacity: 0.5, fontWeight: 400, textTransform: 'none', fontSize: '0.72rem' }}>(puede seleccionar varias)</span></label>
                <div style={{
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
                  background: 'rgba(255,255,255,0.04)', padding: '10px 12px',
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px'
                }}>
                  {REGION_VENTA_OPTIONS.map(opt => {
                    const selected = parseRegions(formData.region_venta);
                    const isChecked = selected.includes(opt);
                    return (
                      <label key={opt} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                        cursor: 'pointer', fontSize: '0.78rem', lineHeight: '1.3',
                        color: isChecked ? '#93c5fd' : 'rgba(255,255,255,0.6)',
                        fontWeight: isChecked ? 600 : 400,
                        transition: 'color 0.15s'
                      }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const next = isChecked
                              ? selected.filter(r => r !== opt)
                              : [...selected, opt];
                            setFormData({ ...formData, region_venta: joinRegions(next) });
                          }}
                          style={{ marginTop: '2px', accentColor: '#3b82f6', flexShrink: 0 }}
                        />
                        {opt}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Cargo 2 */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Cargo 2</label>
                <select
                  style={selectStyle}
                  value={formData.cargo2 || ''}
                  onChange={e => setFormData({ ...formData, cargo2: e.target.value })}
                >
                  <option value="">— Seleccionar —</option>
                  {CARGO2_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Región */}
              <div style={{ marginBottom: '1.8rem' }}>
                <label style={labelStyle}>Región</label>
                <input style={inputStyle} type="text" placeholder="Ej: Región Capital..."
                  value={formData.region || ''} onChange={e => setFormData({ ...formData, region: e.target.value })} />
              </div>

              {/* Botones */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={handleCloseModal}
                  style={{
                    padding: '8px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                    background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600
                  }}>
                  Cancelar
                </button>
                <button type="submit"
                  style={{
                    padding: '8px 22px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    background: '#3b82f6', color: 'white', fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(59,130,246,0.4)'
                  }}>
                  {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <footer className="status-footer">
        {loading ? 'CARGANDO USUARIOS...' : `USUARIOS: ${filteredUsuarios.length} DE ${usuarios.length}`}
      </footer>
    </motion.div>
  );
};

const labelStyle = {
  display: 'block', fontSize: '0.78rem', fontWeight: 700,
  opacity: 0.7, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'white'
};

const inputStyle = {
  width: '100%', padding: '8px 12px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px', background: 'rgba(255,255,255,0.05)',
  color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.2s'
};

const selectStyle = {
  ...inputStyle,
  background: '#1e2130',
  color: '#e2e8f0',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: '36px'
};

export default AdminUsuarios;